import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Terminal, Copy, CheckCircle2, Server } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/hooks/use-toast';

type Props = {
  config: Record<string, any>;
  onComplete: () => void;
  onBack: () => void;
};

export function PostInstallStep({ config, onComplete, onBack }: Props) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const serverIp = config.IDRAC_ENDPOINT || '<server-ip>';

  const commands = [
    {
      title: 'Step 1: SSH into Deployed Server',
      command: `ssh ubuntu@${serverIp}`,
      description: 'Connect to the newly provisioned server',
    },
    {
      title: 'Step 2: Run OS Hardening Script',
      command: 'cd /opt/setup-scripts && sudo ./post-os-setup.sh',
      description: 'Apply security baselines and OS configurations',
    },
    {
      title: 'Step 3: Install Gaudi Drivers',
      command: 'sudo ./install-gaudi-drivers.sh',
      description: 'Install Intel Gaudi AI accelerator drivers',
    },
    {
      title: 'Step 4: Configure Inference Runtime',
      command: 'sudo ./setup-inference-runtime.sh',
      description: 'Set up model serving framework and endpoints',
    },
    {
      title: 'Step 5: Verify Installation',
      command: 'sudo ./verify-deployment.sh',
      description: 'Run health checks and validation tests',
    },
  ];

  const copyCommand = async (command: string, index: number) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
      toast({ title: 'Copied to clipboard' });
    } catch (err) {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Server className="h-5 w-5 text-primary" />
            <div>
              <CardTitle>Post-Installation Configuration</CardTitle>
              <CardDescription>
                Complete the in-band setup via SSH
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {commands.map((cmd, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">{cmd.title}</h4>
              </div>
              <p className="text-xs text-muted-foreground">{cmd.description}</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 rounded-md bg-muted font-mono text-sm flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-muted-foreground" />
                  <code>{cmd.command}</code>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyCommand(cmd.command, index)}
                >
                  {copiedIndex === index ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
        <AlertDescription className="text-xs text-blue-800 dark:text-blue-200">
          Each script includes logging to /var/log/dell-inference-setup/. Review these logs 
          if any step fails.
        </AlertDescription>
      </Alert>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onComplete}>
          Setup Complete
        </Button>
      </div>
    </div>
  );
}
